<script setup>
import { computed, inject, ref } from 'vue'
import SingleTaskStatus from './SingleTaskStatus.vue'
import { asyncComputed } from '@/utils/asyncComputed'
import { ownershipFilingsGet } from '@/pages/manage/providers/sec/ownership/filings/services'
import { getExecutive } from '@/services/executiveService'
import BoardMembershipTable from '@/components/executives/BoardMembershipTable.vue'
import WorkHistoryTable from '@/components/executives/WorkHistoryTable.vue'
import { getFiler } from '@/services/providers/sec/filersService.js'
import taskService from '@/services/manage/taskService'
import entityProviderIdService from '@/services/manage/entityProviderIdService'

const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
})

const executive = asyncComputed(() => getExecutive(props.item.primaryEntityId))
const filing = asyncComputed(() => ownershipFilingsGet(props.item.secondaryEntityId))
const issuer = asyncComputed(() => {
    if (!filing.value) {
        return null
    }

    return getFiler(filing.value.issuerCik)
}, null)

const updating = computed(() => false)

const status = ref(null)

const flash = inject('flash')

const committing = ref(false)

async function clearTask() {
    committing.value = true
    try {
        await taskService.complete(props.item)
    } catch (ex) {
        console.error(ex)
    }
    committing.value = false
}

async function associateCik() {
    committing.value = true

    try {
        const { ok, status } = await entityProviderIdService.linkProvider(
            props.item.primaryEntityType,
            props.item.primaryEntityId,
            6,
            filing.value.reportingOwnerCik
        )

        // if something came and created the link
        if (ok || status === 409) {
            flash('Added CIK', 'success')
            await clearTask()
            return
        }
    } catch (ex) {
        console.error(ex)
    } finally {
        committing.value = false
    }

    flash('Failed to add CIK', 'error')
}
</script>
<template>
    <div class="loading-container">
        <loading :active="updating" :is-full-page="false" />
        <h2>CIK Match Found</h2>
        <SingleTaskStatus :taskId="item.id" :seconds="300" ref="status" class="task-status left" />
        <div class="task-body">
            <dl v-if="filing">
                <dt>Owner</dt>
                <dd>
                    {{ filing.reportingOwnerName }}
                    (<router-link
                        :to="{ name: 'manage-providers-sec-filer', params: { cik: filing.reportingOwnerCik } }"
                        class="primary-link"
                    >
                        {{ filing.reportingOwnerCik }} </router-link
                    >)
                </dd>
                <dt>Issuer</dt>
                <dd>
                    {{ issuer?.name ?? filing.issuerName }}
                    (<router-link
                        :to="{ name: 'manage-providers-sec-filer', params: { cik: filing.issuerCik } }"
                        class="primary-link"
                    >
                        {{ filing.issuerCik }} </router-link
                    >)

                    <ul v-if="issuer?.formerNames">
                        <li v-for="(formerName, index) in issuer.formerNames" :key="`fn-${index}`">
                            {{ formerName.name }}
                            <em>
                                ({{ new Date(formerName.from).toLocaleDateString() }} -
                                {{ new Date(formerName.to).toLocaleDateString() }})
                            </em>
                        </li>
                    </ul>
                </dd>
                <dt>Director</dt>
                <dd>{{ filing.isDirector }}</dd>
                <dt>Title</dt>
                <dd>{{ filing.officerTitle }}</dd>
                <dt>Acceptance Date</dt>
                <dd>{{ new Date(filing.acceptanceDateTime).toLocaleDateString() }}</dd>
            </dl>
            <h3>Work History</h3>
            <WorkHistoryTable :jobs="executive.jobs" v-if="executive" />
            <h3>Boards</h3>
            <BoardMembershipTable :boards="executive.boards" v-if="executive" />
        </div>
        <div class="buttons">
            <button class="danger" :disabled="committing" @click="clearTask">Ignore CIK</button>
            <button class="primary" :disabled="committing" @click="associateCik">Associate CIK</button>
        </div>
    </div>
</template>
<style scoped>
.loading-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
}

.task-status {
    position: absolute;
    top: 0;
    right: 8px;
    z-index: 1;
}

.task-body {
    display: flex;
    flex-direction: column;
    flex: 1 1 1px;
}

.task-body dl {
    align-items: center;
    display: grid;
    grid-template-columns: max-content 1fr;
    grid-auto-rows: initial;
}

.task-body dt {
    background-color: var(--color-primary-100);
    font-weight: bold;
    padding: 8px 16px;
    height: 100%;
}

.task-body dd {
    height: auto;
    padding: 8px 16px;
}

dt:nth-child(n + 1) {
    border-top: 2px solid white;
}

h3 {
    margin: 8px 0;
}

.work-history-table :deep(.toggle) {
    width: 24px;
}

.work-history-table :deep([data-label='Alpha']),
.work-history-table :deep([data-label='Ticker']) {
    display: none;
}

.work-history-table :deep([data-label='Dates']) {
    width: 112px;
}

.buttons {
    display: flex;
    gap: 16px;
    padding: 8px;
}
</style>
